import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Grid,
  Button,
  IconButton,
  createStyles,
  Theme,
  makeStyles,
  useTheme,
  useMediaQuery,
  Badge,
  TextField,
  InputAdornment,
  Hidden,
  ThemeProvider,
} from "@material-ui/core";
import { ArrowBack, Search, Room, Storefront } from "@material-ui/icons";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import { Autocomplete } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import "../../i18n/config";

import { matchSorter } from "match-sorter";
import ItemCardModal from "../Templates/ItemCardModal/ItemCardModal";
import { PFXCleverlynkGroupType } from "../../models/Interfaces";
import { useLocation } from "react-router-dom";
import CategoriesDrawer from "./Drawer";
import { useSelector } from "react-redux";
import {
  selectItems,
  selectTemplateType,
  PFXTemplateType,
  selectBanner,
  PFXCategoryItem,
  selectDisableNavbarFade,
  selectItemsEntities,
} from "../../redux/slices/templateSlice";
import {
  ErrorType,
  selectCartIsOpen,
  selectCleverlynk,
  selectCleverlynkGroup,
  selectCleverlynkStage,
  selectError,
  selectItemsInCart,
  selectLogo,
  selectNoLanding,
} from "../../redux/slices/cleverlynkSlice";
import useCleverlynkFunctions from "../../hooks/useCleverlynkFunctions";
import useTemplateStyles from "../../hooks/useTemplateStyles";
import useTemplateFunctions from "../../hooks/useTemplateFunctions";
import { useClynkTheme } from "../../hooks/useClynkTheme";
import { useCleverlynkContext } from "../../contexts/CleverlynkContext";
import GroupPopup from "./GroupPopup";
import ShoppingCart from "../ShoppingCart/ShoppingCart2";
import useLandingFunctions from "../../hooks/useLandingFunctions";

interface IHeader {
  atTemplate?: boolean;
  atCheckout?: boolean;
  atLanding?: boolean;
  atCoverage?: boolean;
}

const Header = ({ atTemplate, atCheckout, atLanding, atCoverage }: IHeader) => {
  const [groupPopup, setGroupPopup] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<PFXCategoryItem | string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const templateType = useSelector(selectTemplateType);
  const logo = useSelector(selectLogo);
  const items = useSelector(selectItems);
  const itemsEntities = useSelector(selectItemsEntities);
  const itemsInCart = useSelector(selectItemsInCart);
  const cleverlynkGroup = useSelector(selectCleverlynkGroup);
  const error = useSelector(selectError);
  const banner = useSelector(selectBanner);
  const disableNavbarFade = useSelector(selectDisableNavbarFade);
  const cartIsOpen = useSelector(selectCartIsOpen);
  const noLanding = useSelector(selectNoLanding);

  const { t } = useTranslation(["header", "common"]);

  const cleverlynkContext = useCleverlynkContext();

  const { handleModifyCartIsOpen, handleGoToPreviousStage, handleSetCleverlynkStage } = useCleverlynkFunctions();
  const { handleModifyItemToShow, closeItemDialog } = useTemplateFunctions();
  const { navbarStyles } = useTemplateStyles();
  const { handleGoToLanding, handleGoToCatalog } = useLandingFunctions();

  const theme = useTheme();
  const { createClynkTheme } = useClynkTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const persistentCart = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const animateChipRef = useRef<boolean>(false);

  useEffect(() => {
    if (!persistentCart) handleModifyCartIsOpen(false);
  }, [persistentCart]);

  useEffect(() => {
    animateChipRef.current = true;
    const t = setTimeout(() => {
      animateChipRef.current = false;
    }, 1000);
    return () => clearTimeout(t);
  }, [itemsInCart]);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      appBar: {
        zIndex: 1303,
        padding: 0,
        backgroundColor: atTemplate && banner && !disableNavbarFade ? "transparent" : navbarStyles?.headerColor,
        boxShadow:
          atTemplate && banner && !disableNavbarFade
            ? "none"
            : "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%);",
        top: 0,
        transitionProperty: "top, width",
        width: !isMobile && cartIsOpen ? "calc(100% - 375px)" : "100%",
        left: 0,
        transitionDuration: "400ms",
      },
      search: {
        width: 250,
        borderRadius: 5,
        backgroundColor: "#fff",
        [theme.breakpoints.up("md")]: {
          width: 300,
        },
        [theme.breakpoints.down("xs")]: {
          width: "100%",
        },
      },
      autoCompleteSuggestions: {
        zIndex: 1500,
      },
      logo: {
        transitionProperty: "height, width",
        transitionDuration: "400ms",
        marginLeft: "auto",
        marginRight: "auto",
        display: "inline-block",
        cursor: "pointer",
      },
      headerIcon: {
        padding: 8,
      },
      "@keyframes flash": {
        "0%": {
          backgroundColor: theme.palette.primary.main,
          color: "white",
        },
        "50%": {
          backgroundColor: "#51d67d",
          color: "white",
        },
        "100%": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
      },
      animateChip: {
        animation: "$flash 1s ease 2",
      },
    })
  );

  const classes = useStyles();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const it = searchParams.get("it");
    if (it) {
      const item = itemsEntities[it];
      handleModifyItemToShow(item);
    }
  }, []);

  const iconColor = React.useMemo(() => {
    let hex: string = navbarStyles?.headerColor! ?? "#FFFFFF";

    if (hex.indexOf("#") === 0) {
      hex = hex.slice(1);
    }
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6) {
      return "#FFFFFF";
    }
    var r = parseInt(hex.slice(0, 2), 16),
      g = parseInt(hex.slice(2, 4), 16),
      b = parseInt(hex.slice(4, 6), 16);

    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? "#383535" : "#FFFFFF";
  }, [navbarStyles]);

  useEffect(() => {
    const elem = document.getElementById("clynk-header");
    const clynkHeaderLogo = document.getElementById("clynks-header-logo");
    if ((atLanding || (atTemplate && banner)) && !disableNavbarFade) {
      setTimeout(() => {
        if (elem) {
          elem.style.boxShadow = "none";
          elem.style.backgroundColor = "transparent";
        }
        if (clynkHeaderLogo) {
          clynkHeaderLogo.style.height = "70px";
        }
      }, 50);
    }
  }, [banner, atTemplate, atLanding, disableNavbarFade, navbarStyles?.headerColor]);

  let prevScrollpos = window.pageYOffset;

  window.onscroll = function () {
    function hexToRGB(hex, alpha) {
      var r = parseInt(hex.slice(1, 3), 16),
        g = parseInt(hex.slice(3, 5), 16),
        b = parseInt(hex.slice(5, 7), 16);
      return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
    }

    if (atTemplate || atLanding) {
      let currentScrollPos = window.pageYOffset;
      const clynkHeader = document.getElementById("clynk-header");
      const clynkHeaderLogo = document.getElementById("clynks-header-logo");

      if (banner && !disableNavbarFade && currentScrollPos <= 500 && clynkHeader) {
        const a = Math.min(200, currentScrollPos) / 100;
        if (atTemplate || atLanding) {
          clynkHeader.style.backgroundColor = `${hexToRGB(navbarStyles?.headerColor, a)}`;
        }
        if (currentScrollPos < 75 && (atTemplate || atLanding)) {
          clynkHeader.style.boxShadow = "none";
          if (clynkHeaderLogo) clynkHeaderLogo.style.height = "70px";
        } else {
          clynkHeader.style.boxShadow = "0pt 1pt 4px 0px #00000061";
          if (clynkHeaderLogo) clynkHeaderLogo.style.height = "50px";
        }
      }

      const categoriesMenu = document.getElementById("categories-menu");
      const categoriesMenuTabs = document.getElementById("categories-menu-tabs");
      if (categoriesMenu && categoriesMenuTabs && clynkHeader) {
        if (prevScrollpos > currentScrollPos && currentScrollPos < 100) {
          categoriesMenuTabs.style.width = "98%";
          categoriesMenuTabs.style.borderRadius = "3px";
          categoriesMenu.style.top = "70px";
        } else if (currentScrollPos >= 100) {
          categoriesMenuTabs.style.width = "100%";
          categoriesMenuTabs.style.borderRadius = "0px";
          categoriesMenu.style.top = "54px";
        }
      }
      prevScrollpos = currentScrollPos;
    }
  };

  useEffect(() => {
    const elem = document.getElementById("clynk-header");
    if (elem && atTemplate && banner && !disableNavbarFade) {
      elem.style.backgroundColor = `${navbarStyles?.headerColor}00`;
      elem.style.boxShadow = "none";
    }
  }, [navbarStyles]);

  const goHome = () => {
    window.scroll({
      top: 0,
      behavior: "smooth",
    });
    noLanding ? handleGoToCatalog() : handleGoToLanding();
    // handleSetCleverlynkStage(1);
  };

  const searchableItems = useMemo(() => {
    return items.filter(i => !!!i.soldOut);
  }, [items]);

  const renderAutoComplete = () => {
    return (
      <Autocomplete
        className={classes.search}
        classes={{ popper: classes.autoCompleteSuggestions }}
        freeSolo
        fullWidth
        options={searchableItems}
        getOptionLabel={(item: any) => item?.name}
        value={searchValue}
        filterOptions={(options, { inputValue }) => matchSorter(options, inputValue, { keys: ["name"] })}
        onChange={(_, val) => {
          if (typeof val !== "string") handleModifyItemToShow(val as PFXCategoryItem);
          setSearchValue(val);
          setTimeout(() => {
            setSearchValue("");
          }, 100);
        }}
        inputValue={inputValue}
        onInputChange={(_, val) => setInputValue(val)}
        renderInput={params => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              style: { paddingTop: 4, paddingBottom: 4, height: 43 },
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            placeholder={t(`common:searchProduct`)}
            variant="outlined"
          />
        )}
      />
    );
  };

  if (
    location.pathname.includes("order/summary") ||
    cleverlynkContext?.loading ||
    (error && error?.type !== ErrorType.STORE_CLOSED)
  ) {
    return <div />;
  }

  return !cleverlynkContext?.loading ? (
    <React.Fragment>
      <AppBar id="clynk-header" position="fixed" className={classes.appBar}>
        <Grid container justifyContent="space-between" alignItems="center" style={{ padding: "0px 8px" }}>
          <Grid item xs={3} container wrap="nowrap" justifyContent="flex-start">
            {(atTemplate || atLanding || atCoverage) && <CategoriesDrawer color={iconColor} />}
            {!atTemplate && !atLanding && !atCoverage && (
              <Button onClick={handleGoToPreviousStage}>
                <ArrowBack style={{ color: iconColor }} />
              </Button>
            )}
            {atTemplate && cleverlynkGroup?.id && (
              <IconButton
                className={classes.headerIcon}
                onClick={() => {
                  setGroupPopup(p => !p);
                }}></IconButton>
            )}
          </Grid>
          <Grid container item xs={6} justifyContent="center">
            <Grid item>
              <img
                onClick={goHome}
                src={logo}
                height={!atTemplate || !banner || disableNavbarFade ? 50 : 70}
                alt="logo"
                id="clynks-header-logo"
                className={classes.logo}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end" alignItems="center" item xs={3} wrap="nowrap" spacing={1}>
            <Hidden only="xs">
              <Grid item>{renderAutoComplete()}</Grid>
            </Hidden>
            <Grid item container wrap="nowrap" justifyContent={!isMobile ? "center" : "flex-end"} xs={isMobile ? 4 : 2}>
              <Hidden smUp>
                <IconButton className={classes.headerIcon} onClick={() => setSearchOpen(s => !s)}>
                  <Search style={{ color: iconColor }} />
                </IconButton>
              </Hidden>
              {!atCheckout && !cartIsOpen && (
                <IconButton className={classes.headerIcon} onClick={() => handleModifyCartIsOpen(true)}>
                  <Badge
                    badgeContent={itemsInCart.reduce((p, n) => (n?.quantity ?? 0) + p, 0)}
                    color="primary"
                    classes={{
                      badge: animateChipRef.current ? classes.animateChip : "",
                    }}>
                    <ShoppingCartIcon style={{ color: iconColor }} />
                  </Badge>
                </IconButton>
              )}
            </Grid>
          </Grid>
          {searchOpen && (
            <Hidden smUp>
              <Grid item xs={12}>
                {renderAutoComplete()}
              </Grid>
            </Hidden>
          )}
        </Grid>
        <ThemeProvider theme={createClynkTheme()}>
          <ItemCardModal
            setClose={() => {
              handleModifyItemToShow(undefined);
              closeItemDialog();
            }}
          />
        </ThemeProvider>
      </AppBar>
      <ShoppingCart isMobile={isMobile} />
    </React.Fragment>
  ) : (
    <AppBar position="fixed">
      <Grid container justifyContent="center">
        <Grid item>
          <img
            onClick={goHome}
            src={logo}
            height={50}
            alt="logo"
            style={{
              marginLeft: "auto",
              marginRight: "auto",
              display: "inline-block",
              cursor: "pointer",
            }}
          />
        </Grid>
      </Grid>
    </AppBar>
  );
};

export default Header;
